"use client";

// AccountIdentity — WHO YOU ARE, in one place. The old split (a "profile" tab
// holding the photo + name, an "account" tab holding just the email) made you
// hunt for half of yourself; this composes them: the cell you wear everywhere,
// the name every surface says, the address you sign in with, and a quiet ledger
// of what innernet is actually holding for you.
//
// No boxes — the avatar floats on the ground, the fields are THE one soft field
// (law_inputs), everything else is hairlines and type.

import { useEffect, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import CellAvatar from "../CellAvatar";
import { Field, PillButton } from "../ui";
import SignOutButton from "../../app/dashboard/SignOutButton";
import EmailCodeEntry from "../EmailCodeEntry";

const num = (n: number) => n.toLocaleString();

export default function AccountIdentity({
  email,
  initialName,
  initialAvatarUrl,
  cells,
  projectCount,
  roomCount,
  parkedCount,
  lastActivity,
}: {
  email: string;
  initialName: string | null;
  initialAvatarUrl: string | null;
  cells: number;
  projectCount: number;
  roomCount: number;
  connectionCount: number;
  parkedCount: number;
  lastActivity: string | null;
}) {
  const router = useRouter();
  const fileRef = useRef<HTMLInputElement>(null);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(initialAvatarUrl);
  const [name, setName] = useState(initialName ?? "");
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  // THE ADDRESS — how you sign in, and changing it (app/api/account/identity).
  // A change is only ever a request: the link goes to the new address (and the
  // current one), and nothing moves until it is confirmed.
  const [ident, setIdent] = useState<{ pending: string | null; providers: string[]; since: string | null; verified: boolean; tester?: boolean } | null>(null);
  const [emailOpen, setEmailOpen] = useState(false);
  const [nextEmail, setNextEmail] = useState("");
  const [emailBusy, setEmailBusy] = useState(false);
  const [emailNote, setEmailNote] = useState<{ ok: boolean; text: string } | null>(null);
  useEffect(() => {
    let alive = true;
    fetch("/api/account/identity")
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => { if (alive && j) setIdent({ pending: j.pending ?? null, providers: j.providers ?? [], since: j.since ?? null, verified: j.verified !== false, tester: j.tester === true }); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);
  // an address not yet confirmed (signup lets you in first): the same letter again, link and code
  const [verifyBusy, setVerifyBusy] = useState(false);
  const [verifySent, setVerifySent] = useState(false);
  const sendVerify = async () => {
    setVerifyBusy(true); setEmailNote(null);
    try {
      const res = await fetch("/api/account/identity", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ verify: true }) });
      const j = await res.json().catch(() => ({}));
      if (!res.ok) setEmailNote({ ok: false, text: j.error ?? "that didn’t send. please try again in a minute." });
      else if (j.verified) setIdent((v) => (v ? { ...v, verified: true } : v));
      else { setVerifySent(true); setEmailNote({ ok: true, text: `a link is on its way to ${j.sentTo ?? email}. tap it, or type the code from it below, and your address is confirmed.` }); }
    } catch {
      setEmailNote({ ok: false, text: "that didn’t send. please try again in a minute." });
    }
    setVerifyBusy(false);
  };

  const changeEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    setEmailBusy(true);
    setEmailNote(null);
    try {
      const res = await fetch("/api/account/identity", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ email: nextEmail }) });
      const j = (await res.json().catch(() => ({}))) as { error?: string; pending?: string };
      if (!res.ok) { setEmailNote({ ok: false, text: j.error ?? "that didn’t go through. please try again." }); return; }
      setIdent((cur) => ({ providers: cur?.providers ?? [], since: cur?.since ?? null, verified: cur?.verified ?? true, pending: j.pending ?? nextEmail }));
      setEmailOpen(false);
      setNextEmail("");
      setEmailNote({ ok: true, text: `a link is on its way to ${j.pending ?? nextEmail}, and one to ${email}. please confirm, and the change lands.` });
    } finally {
      setEmailBusy(false);
    }
  };
  const providerWord = (p: string) => (p === "email" ? "email" : p === "google" ? "Google" : p === "github" ? "GitHub" : p === "apple" ? "Apple" : p);

  // what the rest of the shell will call you right now.
  const shownName = name.trim() || initialName || email.split("@")[0];

  const upload = async (file: File) => {
    if (busy) return;
    setBusy(true);
    setNote(null);
    try {
      const res = await fetch("/api/me/avatar", {
        method: "POST",
        headers: { "Content-Type": file.type || "application/octet-stream" },
        body: file,
      });
      const json = (await res.json()) as { ok?: boolean; url?: string; error?: string };
      if (json.ok && json.url) {
        setAvatarUrl(json.url);
        setNote("that's you now — every surface follows.");
        router.refresh();
      } else {
        setNote(json.error ?? "that didn't take — try another image.");
      }
    } catch {
      setNote("that didn't take — try another image.");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await fetch("/api/me/avatar", { method: "DELETE" });
      setAvatarUrl(null);
      setNote("back to the living gradient.");
      router.refresh();
    } finally {
      setBusy(false);
    }
  };

  // the name, edited where it is read (John, 2026-10-27; everyone since 10-28): the pencil opens it, enter or leaving keeps it, esc puts it back
  const [editingName, setEditingName] = useState(false);
  const saveName = async () => {
    setEditingName(false);
    const trimmed = name.trim();
    if (trimmed === (initialName ?? "")) return;
    try {
      await fetch("/api/me/avatar", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ display_name: trimmed }),
      });
      setNote(trimmed ? `everywhere says ${trimmed} now.` : "back to your address.");
      router.refresh();
    } catch {
      /* quiet — the field keeps the typed value */
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "2.4rem", maxWidth: 560 }}>
      {/* ── you, composed: the cell · the name · the address ── */}
      <div style={{ display: "flex", alignItems: "center", gap: "1.6rem", flexWrap: "wrap" }}>
        <button
          type="button"
          data-noswipe
          onClick={() => fileRef.current?.click()}
          disabled={busy}
          title={avatarUrl ? "choose another photo" : "choose a photo"}
          style={{ border: "none", background: "none", padding: 0, cursor: "pointer", lineHeight: 0 }}
        >
          <CellAvatar name={shownName} src={avatarUrl} size={116} />
        </button>
        <div style={{ display: "flex", flexDirection: "column", gap: "0.3rem", minWidth: 0 }}>
          {editingName ? (
            <input
              className="input-soft acct-name-input"
              type="text"
              value={name}
              autoFocus
              maxLength={60}
              placeholder={email.split("@")[0]}
              aria-label="your display name"
              onChange={(e) => setName(e.target.value)}
              onBlur={() => void saveName()}
              onKeyDown={(e) => {
                if (e.nativeEvent.isComposing) return;
                if (e.key === "Enter") (e.target as HTMLInputElement).blur();
                if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); setName(initialName ?? ""); setEditingName(false); }
              }}
            />
          ) : (
            <span className="acct-name">
              <span style={{ fontSize: "var(--t-title, 1.5rem)", letterSpacing: "-0.02em", textWrap: "balance" }}>{shownName}</span>
              <button type="button" className="icon-circle acct-name-edit" data-noswipe onClick={() => setEditingName(true)} aria-label="change your display name" title="change your display name">
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden>
                  <path d="M10.6 2.6a1.6 1.6 0 0 1 2.3 2.3L5.6 12.2 2.5 13l.8-3.1 7.3-7.3Z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
                </svg>
              </button>
            </span>
          )}
          <span style={{ fontSize: "0.92rem", color: "var(--ink-2)", overflowWrap: "anywhere" }}>{email}</span>
          <span style={{ fontSize: "0.8rem", color: "var(--ink-3)" }}>
            {num(cells)} cell{cells === 1 ? "" : "s"} of memory
            {roomCount > 0 ? ` · standing in ${num(roomCount)} room${roomCount === 1 ? "" : "s"}` : ""}
          </span>
          <span style={{ display: "flex", gap: "1rem", alignItems: "center", marginTop: "0.35rem" }}>
            <button className="quiet-link" data-noswipe onClick={() => fileRef.current?.click()} disabled={busy}>
              {busy ? "landing…" : avatarUrl ? "change photo" : "add a photo"}
            </button>
            {avatarUrl && (
              <button className="quiet-link" data-noswipe onClick={remove} disabled={busy} style={{ color: "var(--ink-3)" }}>
                remove
              </button>
            )}
          </span>
          {/* sign out is the section's own act: THE button under who you are */}
          <span style={{ marginTop: "0.9rem" }}>
            <SignOutButton pill />
          </span>
        </div>
        <input
          ref={fileRef}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          style={{ display: "none" }}
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void upload(f);
            e.target.value = "";
          }}
        />
      </div>

      {note && (
        <p style={{ fontSize: "0.82rem", color: "var(--ink-3)", margin: 0 }} aria-live="polite">
          {note}
        </p>
      )}

      <div className="rule-fade" />

      {/* ── the address you sign in with ── */}
      <section style={{ display: "flex", flexDirection: "column", gap: "0.9rem" }}>
        <div style={{ fontSize: "0.78rem", color: "var(--ink-3)" }}>email</div>
        <div style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
          <span style={{ fontSize: "var(--t-body)", color: "var(--ink)", overflowWrap: "anywhere" }}>{email}</span>
          {ident && !ident.verified && (
            // signed up and stepped straight in: the address is confirmed here, whenever they like
            <span style={{ fontSize: "0.85rem", color: "var(--ink-3)" }}>
              not confirmed yet.{" "}
              <button type="button" className="quiet-link" data-noswipe disabled={verifyBusy} onClick={() => void sendVerify()}>
                {verifyBusy ? "sending…" : "send the link"}
              </button>
            </span>
          )}
          {ident?.pending && (
            <span style={{ fontSize: "0.85rem", color: "var(--ink-3)", overflowWrap: "anywhere" }}>
              moving to {ident.pending} once you confirm the link we sent
            </span>
          )}
        </div>
        {emailOpen ? (
          <form onSubmit={changeEmail} style={{ display: "flex", flexDirection: "column", gap: "1.1rem", maxWidth: 380 }}>
            <Field label="the new address" type="email" autoComplete="email" value={nextEmail} onChange={(e) => setNextEmail(e.target.value)} autoFocus />
            <span style={{ fontSize: "0.82rem", color: "var(--ink-3)", textWrap: "pretty" }}>
              we’ll send a confirmation there, and a note to {email}. nothing changes until it’s confirmed.
            </span>
            <div style={{ display: "flex", alignItems: "center", gap: "1.2rem", flexWrap: "wrap" }}>
              <PillButton type="submit" className="cta-pill btn-pill--solid" loading={emailBusy} disabled={!nextEmail.trim()}>
                send the confirmation
              </PillButton>
              <button type="button" className="quiet-link" onClick={() => { setEmailOpen(false); setNextEmail(""); }}>cancel</button>
            </div>
          </form>
        ) : (
          <button type="button" className="quiet-link" data-noswipe style={{ alignSelf: "flex-start" }} onClick={() => { setEmailOpen(true); setEmailNote(null); }}>
            change email
          </button>
        )}
        {verifySent && ident && !ident.verified && <EmailCodeEntry email={email} next="/dashboard" />}
        {emailNote && (
          <p aria-live="polite" style={{ margin: 0, fontSize: "0.85rem", lineHeight: 1.55, color: emailNote.ok ? "var(--ink-2)" : "var(--ink)", paddingLeft: "0.85rem", borderLeft: `2px solid ${emailNote.ok ? "var(--accent)" : "var(--cite-project)"}` }}>
            {emailNote.text}
          </p>
        )}
      </section>

      <div className="rule-fade" />

      {/* ── what innernet holds for you — the ledger, not a dashboard ── */}
      <div className="detail-rows">
        {ident && ident.providers.length > 0 && (
          <div className="detail-row"><span>signs in with</span><span>{ident.providers.map(providerWord).join(" · ")}</span></div>
        )}
        {ident?.tester && (
          // a test account's way back to its first minute, no signup and no email
          <div className="detail-row"><span>test account</span><span><a className="quiet-link" href="/test/start-over">start over</a></span></div>
        )}
        {ident?.since && (
          <div className="detail-row"><span>here since</span><span>{new Date(ident.since).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }).toLowerCase()}</span></div>
        )}
        <div className="detail-row"><span>memory</span><span>{num(cells)} cells</span></div>
        <div className="detail-row">
          <span>projects</span>
          <span>{num(projectCount)}{parkedCount > 0 ? ` · ${num(parkedCount)} parked` : ""}</span>
        </div>
        {roomCount > 0 && <div className="detail-row"><span>rooms</span><span>{num(roomCount)}</span></div>}
        {/* the tools live in connections, which keeps them live */}
        <div className="detail-row">
          <span>last stirred</span>
          <span>{lastActivity ? new Date(lastActivity).toLocaleDateString() : "quiet so far"}</span>
        </div>
      </div>
    </div>
  );
}
